/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import InputField from '@/components/common/InputField';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import {FormHelperText, Grid, MenuItem, Select} from '@mui/material';
import useLocales from '@/hooks/useLocales';
import useError from '@/hooks/useError';
import type {AxiosError} from 'axios';
import {FormControl, FormLabel, TextField} from '@mui/material';
import {
  createFormFields,
  updateFormFields,
  getFieldChoices
} from '@/apis/FormFields';
import LoadingScreen from '@/components/common/LoadingScreen';
import EditOptionWarning from '../EditOptionWarning';
import {toast} from 'react-toastify';
import {
  createPositionFields,
  getPositionFieldChoices,
  updatePositionFields
} from '@/apis/PositionFields';
import {ExclamationTriangleIcon} from '@heroicons/react/20/solid';
import {useNavigate} from 'react-router-dom';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeletePopUp from '@/components/common/DeletePopUp';
interface ErrorResponse {
  non_field_errors: string[];
  choices?: {value: string[]}[];
}

const SelectField = (props: any) => {
  const navigate = useNavigate();
  const {handleErrors} = useError();
  const {translate} = useLocales();
  const [validated, setValidated] = React.useState<boolean>(false);
  const [label, setLabel] = React.useState('');
  const [helpText, setHelpText] = React.useState('');
  const [type, setType] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [isNew, setIsNew] = React.useState(false);
  const [canHaveDependency, setCanHaveDependency] = React.useState(false);
  const [dependency, setDependency] = React.useState(null);
  const [openWarning, setOpenWarning] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [placeholder, setPlaceholder] = React.useState('');
  const [optionList, setOptionList] = React.useState([
    {
      value: '',
      id: ''
    }
  ]);
  const [emptyInputs, setEmptyInputs] = React.useState(
    Array(optionList?.length).fill(false)
  );
  const [displayProductWarning, setDisplayProductWarning] =
    React.useState(false);
  const [displayType, setDisplayType] = React.useState('SELECT');
  const [newPositionField, setNewPositionField] = React.useState<string>('');
  const [fieldType, setFieldType] = React.useState('');

  React.useEffect(() => {
    const path = window.location.pathname;
    const segments = path.split('/');
    const lastSegment = segments[segments.length - 1];
    setNewPositionField(lastSegment);
  }, []);

  React.useEffect(() => {
    const listener = (event: any) => {
      if (event.code === 'Enter' || event.code === 'NumpadEnter') {
        event.preventDefault();
        submitField();
      }
    };

    document.addEventListener('keydown', listener);

    return () => {
      document.removeEventListener('keydown', listener);
    };
  }, [label, helpText, type, placeholder, optionList, displayType]);

  React.useEffect(() => {
    if (props?.id) {
      const noChoices = !props.choices || props.choices.length === 0;

      setLabel(props?.label || '');
      setOptionList(props?.choices || []);
      setType(props?.is_multiple);
      setPlaceholder(props?.placeholder || '');
      setHelpText(props?.help_text || '');
      setDisplayType(props?.display_type);
      setOpenWarning(false);
      setFieldType(props?.field_type);

      if (newPositionField !== 'new_position_field' && noChoices) {
        getFieldsChoices({
          fieldname: 'field__id',
          lookup: 'exact',
          negation: false,
          value: `${props?.id}`
        });
        setCanHaveDependency(props.can_have_dependency);
        setDependency(props.dependency);
      } else {
        getPositionsChoices({
          fieldname: 'field__id',
          lookup: 'exact',
          negation: false,
          value: `${props?.id}`
        });
        setCanHaveDependency(props.can_have_dependency);
        setDependency(props.dependency);
      }
    } else {
      setLabel('');
      setPlaceholder('');
      setHelpText('');
      setType(false);
      setOptionList([
        {
          value: '',
          id: ''
        }
      ]);
      setIsNew(false);
    }
  }, [props, newPositionField]);

  const submitField = () => {
    props?.setRestoreFieldName('');
    if (label.length && optionList.length) {
      setValidated(true);
    } else {
      setValidated(false);
      setLoading(true);
      return;
    }
    const id = props?.id;
    const load = {
      label,
      field_type: 'choice',
      placeholder,
      help_text: helpText,
      choices: optionList.map(el => {
        return {
          value: el.value,
          ...(el.id && {id: el.id})
        };
      }),
      is_multiple: type,
      display_type: displayType
    };
    if (id) {
      if (label.toLocaleLowerCase() === 'product') {
        setDisplayProductWarning(true);
        setLoading(false);
        return;
      }
      updateField();
    } else {
      createField(load);
    }
  };

  const getPositionsChoices = async (payload: any) => {
    setLoading(true);
    const queryLoad = {
      filters: {
        operation: 'AND',
        predicates: [
          {...payload},
          {
            fieldname: 'is_deleted',
            lookup: 'exact',
            negation: false,
            value: 'false'
          }
        ]
      },
      pagination: {
        page: 1,
        page_size: 1000
      }
    };

    await getPositionFieldChoices(queryLoad)
      .then((response: any) => {
        setLoading(false);
        const result = response?.data?.results;
        if (result.length) {
          setOptionList(result);
          setIsNew(true);
        } else {
          setOptionList([
            {
              value: '',
              id: ''
            }
          ]);
        }
      })
      .catch((error: AxiosError) => {
        setLoading(false);
        handleErrors(error?.response?.data);
      });
  };

  const getFieldsChoices = async (payload: any) => {
    setLoading(true);
    const queryLoad = {
      filters: {
        operation: 'AND',
        predicates: [
          {...payload},
          {
            fieldname: 'is_deleted',
            lookup: 'exact',
            negation: false,
            value: 'false'
          }
        ]
      },
      pagination: {
        page: 1,
        page_size: 1000
      }
    };

    await getFieldChoices(queryLoad)
      .then((response: any) => {
        setLoading(false);
        const result = response?.data?.results;
        if (result.length) {
          setOptionList(result);
          setIsNew(true);
        } else {
          setOptionList([
            {
              value: '',
              id: ''
            }
          ]);
        }
      })
      .catch((error: AxiosError) => {
        setLoading(false);
        handleErrors(error?.response?.data);
      });
  };

  const createField = async (payload: any) => {
    setSubmitted(true);
    const updatedEmptyInputs = optionList.map(
      option => option.value.trim() === ''
    );
    setEmptyInputs(updatedEmptyInputs);
    if (props.type === 'position_field' && !updatedEmptyInputs.includes(true)) {
      setLoading(true);
      await createPositionFields(payload)
        .then(async () => {
          setLoading(false);
          props?.setUpdated(true);
          toast.success(translate('submitted_success'));
          setType(false);

          setSubmitted(false);
        })
        .catch((error: AxiosError) => {
          setLoading(false);
          const errorResponse = error?.response?.data as ErrorResponse;
          if (
            errorResponse?.non_field_errors?.length > 0 &&
            errorResponse.non_field_errors[0] ===
              'deleted_field_with_provided_label_already_exists'
          ) {
            props?.setRestoreFieldName(payload.label);
            toast.error(translate('field_already_exist'));
          }
        });
    } else if (
      newPositionField !== 'new_position_field' &&
      !updatedEmptyInputs.includes(true)
    ) {
      setLoading(true);
      await createFormFields(payload)
        .then(async () => {
          setLoading(false);
          props?.setUpdated(true);
          setSubmitted(false);
          setType(false);

          toast.success(translate('submitted_success'));
        })
        .catch((error: AxiosError) => {
          setLoading(false);
          const errorResponse = error?.response?.data as ErrorResponse;
          if (
            errorResponse?.non_field_errors?.length > 0 &&
            errorResponse.non_field_errors[0] ===
              'deleted_field_with_provided_label_already_exists'
          ) {
            props?.setRestoreFieldName(payload.label);
            toast.error(translate('field_already_exist'));
          }
        });
    }
  };

  const updateField = async () => {
    const id = props?.id;
    const payload = {
      label,
      field_type: 'choice',
      placeholder,
      help_text: helpText,
      choices: optionList.map(el => {
        return {
          value: el.value,
          ...(el.id && {id: el.id})
        };
      }),
      is_multiple: type,
      display_type: displayType,
      id
    };
    setSubmitted(true);
    const updatedEmptyInputs = optionList.map(
      option => option.value.trim() === ''
    );
    setEmptyInputs(updatedEmptyInputs);
    if (
      newPositionField === 'new_position_field' &&
      !updatedEmptyInputs.includes(true)
    ) {
      setLoading(true);
      await updatePositionFields(payload)
        .then(async () => {
          props?.setUpdated(true);
          setLoading(false);
          setSubmitted(false);
          setType(false);

          toast.success(translate('submitted_success'));
        })
        .catch((error: AxiosError) => {
          setLoading(false);
          const errorResponse = error?.response?.data as ErrorResponse;
          if (
            errorResponse?.non_field_errors?.length > 0 &&
            errorResponse.non_field_errors[0] ===
              'deleted_field_with_provided_label_already_exists'
          ) {
            props?.setRestoreFieldName(payload.label);
            toast.error(translate('field_already_exist'));
          }
        });
    } else if (
      newPositionField !== 'new_position_field' &&
      !updatedEmptyInputs.includes(true)
    ) {
      setLoading(true);
      await updateFormFields(payload)
        .then(async () => {
          props?.setUpdated(true);
          setLoading(false);
          setSubmitted(false);
          setType(false);

          toast.success(translate('submitted_success'));
        })
        .catch((error: AxiosError) => {
          setLoading(false);
          const errorResponse = error?.response?.data as ErrorResponse;
          if (
            errorResponse?.non_field_errors?.length > 0 &&
            errorResponse.non_field_errors[0] ===
              'deleted_field_with_provided_label_already_exists'
          ) {
            props?.setRestoreFieldName(payload.label);
            toast.error(translate('field_already_exist'));
          }
        });
    }
    setDisplayProductWarning(false);
  };

  const handleClose = () => {
    setDisplayProductWarning(false);
  };

  return (
    <div className="flex flex-col justify-between h-[70vh] overflow-y-scroll">
      {displayProductWarning && (
        <DeletePopUp
          confirmButtonText={translate('update_anyway')}
          header={translate('are_you_sure')}
          title={translate('are_you_sure_edit_product')}
          open={displayProductWarning}
          handleClose={handleClose}
          handleConfirm={() => updateField()}
        />
      )}
      {isNew && (
        <EditOptionWarning
          open={openWarning}
          setOpen={setOpenWarning}
          setIsNew={setIsNew}
        />
      )}
      <div className="new-textfield-container mt-5">
        <div>
          <FormControl className="w-[300px] mb-5">
            <FormLabel className="text-[#212B36] mb-1">
              {translate('label')}
            </FormLabel>
            <TextField
              fullWidth={true}
              value={label}
              size="small"
              className="bg-white"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setLabel(e.target.value);
                setLoading(false);
              }}
              placeholder={translate('label')}
              error={Boolean(loading && label === '')}
              helperText={
                loading && label === '' ? translate('required') : null
              }
            />
          </FormControl>
        </div>
        <div>
          <FormControl className="w-[300px] mb-5">
            <FormLabel className="text-[#212B36] mb-1">
              {translate('placeholder')}
            </FormLabel>
            <TextField
              fullWidth={true}
              value={placeholder}
              size="small"
              className="bg-white"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setPlaceholder(e.target.value);
                setLoading(false);
              }}
              placeholder={translate('placeholder')}
            />
          </FormControl>
        </div>
        <div className="mb-3">
          <div className="text-[16px] text-[#212B36]">
            {translate('options')}
          </div>
          {loading && validated && <LoadingScreen />}
          <div className={`${optionList?.length > 3 && 'mt-2'}`}>
            {optionList?.map((option, index) => (
              <Grid
                key={option?.id || index}
                container={true}
                spacing={2}
                className="w-[300px] items-center"
              >
                <Grid item={true} xs={10}>
                  <div
                    onClick={() => {
                      if (option?.id && isNew) {
                        setOpenWarning(true);
                      }
                    }}
                  >
                    <InputField
                      label={''}
                      defaultValue={option?.value}
                      handleChange={e => {
                        const updatedOptionList = [...optionList];
                        updatedOptionList[index] = {
                          value: e,
                          id: option?.id || ''
                        };
                        setOptionList(updatedOptionList);

                        const updatedEmptyInputs = [...emptyInputs];
                        updatedEmptyInputs[index] = e.trim() === '';
                        setEmptyInputs(updatedEmptyInputs);
                      }}
                      placeholder={`${translate('option')} ${index + 1}`}
                      type={'text'}
                    />
                    {submitted && emptyInputs[index] && (
                      <FormHelperText className="flex items-center ml-0 text-error leading-normal">
                        <ExclamationTriangleIcon className="text-error h-5 mr-1" />
                        {translate('options_cannot_be_blank')}
                      </FormHelperText>
                    )}
                  </div>
                </Grid>
                <Grid item={true} xs={2} className="pt-1">
                  <CloseIcon
                    className="cursor-pointer"
                    onClick={() => {
                      if (
                        option?.id &&
                        isNew &&
                        label.toLocaleLowerCase() === 'product'
                      ) {
                        setOpenWarning(true);
                        return;
                      }
                      setOptionList((prev: any) => {
                        if (prev.length > 1) {
                          prev.splice(index, 1);
                          return [...prev];
                        }
                        return [
                          {
                            value: '',
                            id: ''
                          }
                        ];
                      });
                    }}
                  />
                </Grid>
              </Grid>
            ))}
          </div>
          <div className="flex items-center justify-center w-[300px]">
            <AddIcon
              className="text-[#144A6C] cursor-pointer"
              onClick={() => {
                if (isNew && label.toLocaleLowerCase() === 'product') {
                  setOpenWarning(true);
                }
                setOptionList((prev: any) => [
                  ...prev,
                  {
                    value: '',
                    id: ''
                  }
                ]);
              }}
            />
          </div>
        </div>
        <div>
          <FormControl className="w-[300px] mb-5">
            <FormLabel className="text-[#212B36] mb-1">
              {translate('display_type')}
            </FormLabel>
            <Select
              className="bg-white rounded-lg user-container"
              size="small"
              multiple={false}
              value={displayType}
              onChange={(e: any) => {
                setDisplayType(e.target.value);
                setLoading(false);
              }}
              displayEmpty
            >
              <MenuItem value="SELECT">{translate('dropdown')}</MenuItem>
              <MenuItem
                value="CHECKBOX"
                disabled={props.type === 'position_field'}
              >
                {translate('radio_button')}
              </MenuItem>
            </Select>
          </FormControl>
        </div>
        <FormControlLabel
          control={
            <Switch
              checked={type}
              disabled={props?.id || displayType === 'CHECKBOX'}
              onClick={(e: any) => setType(e?.target?.checked)}
            />
          }
          label={translate('multi_select')}
        />

        {!props?.is_standard && (
          <>
            {!dependency && canHaveDependency && (
              <>
                <p className="text-[16px] leading-6 mb-2">
                  {translate('field_dependency')}
                </p>
                <div>
                  <Button
                    onClick={() => {
                      const basePath = location.pathname.includes(
                        '/settings/new_position_field'
                      )
                        ? 'position_dependency'
                        : 'field_dependency';
                      navigate(`/dashboard/settings/${basePath}/${props.id}`);
                    }}
                    className="font-open-sans font-xs normal-case underline pl-0 pt-0"
                  >
                    + {translate('add_field_dependency')}
                  </Button>
                </div>
              </>
            )}
            {dependency && (
              <>
                <p className="text-[16px] leading-6 mb-2 mt-6">
                  {translate('field_dependency')}
                </p>
                <div className="align-middle border border-grey rounded-lg w-[300px] h-[40px]">
                  <Button
                    onClick={() => {
                      const basePath = location.pathname.includes(
                        '/settings/new_position_field'
                      )
                        ? 'position_dependency'
                        : 'field_dependency';
                      navigate(`/dashboard/settings/${basePath}/${props.id}`);
                    }}
                    className="flex text-subtext justify-between pl-2 font-open-sans font-sm normal-case w-full h-full"
                  >
                    {dependency?.depends_on.label}
                    <ChevronRightIcon className="h-8 w-8 cursor-pointer" />
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </div>
      <div className="flex">
        <div>
          {props?.is_standard && (
            <div className="flex">
              <ExclamationTriangleIcon
                className="h-5 w-5 text-dark-yellow"
                aria-hidden="true"
              />
              <span className="text-dark-yellow">
                {translate('not_editable')}
              </span>
            </div>
          )}
          <Button
            variant="contained"
            size="small"
            color="inherit"
            className="bg-[#144A6C] mr-5 px-7 h-[20px] text-white normal-case"
            onClick={submitField}
            disabled={(!validated && loading) || props?.is_standard}
          >
            {translate('save')}
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="inherit"
            className="bg-white mr-5 px-5 text-[#144A6C] h-[20px] normal-case border-0"
            onClick={() => {
              setLabel('');
              setPlaceholder('');
              setHelpText('');
            }}
          >
            {translate('cancel')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SelectField;
